import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { icons } from "@patient-watch/icons";
import { commonComponents as cc } from "@/components/common";
import { ui } from "@patient-watch/ui";
import { diariesByPatientQueryOptions, fetchPatientDiariesFresh } from "@/features/diaries/api";
import type { StatefulDiaryT } from "@patient-watch/types";
import { useDiariesByPatientQuery } from "@/features/diaries/api/diaryQueries";
import DiaryList from "@/features/diaries/components/DiaryList";
import JourneyProgress from "@/features/onboarding/components/JourneyProgress";
import { useNav } from "@/utils/nav";
import { useQueryClient } from "@tanstack/react-query";
import { diaryKeys } from "@/features/diaries/api/diaryKeys";
import { useDiaryCopy } from "@/features/i18n/diaryCopy";

const DiariesContent = () => {
    const { auth } = Route.useRouteContext();
    const copy = useDiaryCopy();
    const { data: diaries } = useDiariesByPatientQuery(auth.id);
    const queryClient = useQueryClient();

    const hasNoDiaries = diaries.length === 0;

    const refresh = () =>
        queryClient.invalidateQueries({
            queryKey: diaryKeys.byPatient(auth.id),
        });

    return (
        <cc.AppPageLayout className="md:py-8">
            <cc.Title words={copy.diaries}>
                {hasNoDiaries && (
                    <ui.Card className="mt-4">
                        <ui.CardHeader>
                            <ui.CardTitle>{copy.listEmptyTitle}</ui.CardTitle>
                            <ui.CardDescription>{copy.listEmptyDescription}</ui.CardDescription>
                        </ui.CardHeader>
                        <ui.CardContent>
                            <ui.Button data-testid="check-again-button" onClick={refresh}>
                                {copy.listCheckAgain}
                            </ui.Button>
                        </ui.CardContent>
                    </ui.Card>
                )}
            </cc.Title>
            <JourneyProgress className="mb-4" />
            {!hasNoDiaries && <DiaryList diaries={diaries} />}
        </cc.AppPageLayout>
    );
};

const Diaries = () => {
    const copy = useDiaryCopy();
    useNav([{ label: copy.diaries, route: { to: "/diaries" } }]);

    return (
        <Suspense
            fallback={
                <cc.AppPageLayout className="flex justify-center md:py-8">
                    <icons.JazzySpinner />
                </cc.AppPageLayout>
            }
        >
            <DiariesContent />
        </Suspense>
    );
};

export const Route = createFileRoute("/_app/diaries/")({
    // Do not hard-redirect to onboarding / setup from the diaries list.
    // Incomplete profile steps stay soft (badges, profile CTAs); resume routing is for post-login only.
    loader: async ({ context }) => {
        const patientId = context.auth.id;
        const options = diariesByPatientQueryOptions(patientId);
        const cached = context.queryClient.getQueryData<StatefulDiaryT[]>(options.queryKey);

        if (!cached?.length) {
            return fetchPatientDiariesFresh(context.queryClient, patientId);
        }

        return context.queryClient.ensureQueryData(options);
    },
    component: Diaries,
});
